import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Printer } from 'lucide-react';
import { useClinicSettings } from '@/lib/store/clinic-settings';
import { Visit } from '../types/visit';
import { HistoricalPrescriptionsSection } from './HistoricalPrescriptionsSection';

interface Prescription {
  medication: string;
  dosage: string;
  frequency: string;
  duration: string;
  notes?: string;
}

interface PrescriptionFormProps {
  value: Prescription[];
  onChange: (value: Prescription[]) => void;
  visit: Visit;
  patientId: string;
  onPrescriptionStatusChange: (prescription: { id: string; medication: string; active: boolean }) => void;
}

// Mock data for medications
const medications = [
  { id: '1', name: 'Amoxicillin' },
  { id: '2', name: 'Ibuprofen' },
  { id: '3', name: 'Omeprazole' },
];

export function PrescriptionForm({ value, onChange, visit, patientId, onPrescriptionStatusChange }: PrescriptionFormProps) {
  const [selectedMed, setSelectedMed] = useState('');
  const [selectedDosage, setSelectedDosage] = useState('');
  const [frequency, setFrequency] = useState('');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const { clinicName } = useClinicSettings();

  const handleAdd = () => {
    if (selectedMed && selectedDosage && frequency) {
      const medication = medications.find(m => m.id === selectedMed);
      const newPrescription: Prescription = {
        medication: medication?.name || '',
        dosage: selectedDosage,
        frequency,
        duration,
        notes: notes || undefined
      };
      
      onChange([...value, newPrescription]);
      
      // Reset form
      setSelectedMed('');
      setSelectedDosage('');
      setFrequency('');
      setDuration('');
      setNotes('');
    }
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Prescription</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                max-width: 800px;
                margin: 0 auto;
              }
              .header {
                text-align: center;
                margin-bottom: 30px;
                padding-bottom: 10px;
                border-bottom: 2px solid #000;
              }
              .clinic-name {
                font-size: 24px;
                font-weight: bold;
                margin-bottom: 10px;
              }
              .patient-info {
                text-align: left;
                margin: 20px 0;
                padding: 15px;
                background-color: #f8f9fa;
                border-radius: 4px;
              }
              .patient-info p {
                margin: 5px 0;
              }
              .medication {
                margin-bottom: 20px;
                padding-bottom: 10px;
                border-bottom: 1px solid #eee;
              }
              .medication:last-child {
                border-bottom: none;
              }
              @media print {
                body {
                  padding: 0;
                }
                .patient-info {
                  background-color: transparent;
                  border: 1px solid #ddd;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              ${clinicName ? `<div class="clinic-name">${clinicName}</div>` : ''}
              <h2>Medical Prescription</h2>
              <p>Date: ${new Date().toLocaleDateString()}</p>
            </div>
            ${visit.patient ? `
              <div class="patient-info">
                <p><strong>Patient Name:</strong> ${visit.patient.firstName} ${visit.patient.lastName}</p>
                <p><strong>Date of Birth:</strong> ${new Date(visit.patient.dateOfBirth).toLocaleDateString()}</p>
              </div>
            ` : ''}
            <div class="content">
              ${value.map(prescription => `
                <div class="medication">
                  <h3>${prescription.medication} - ${prescription.dosage}</h3>
                  <p>Frequency: ${prescription.frequency}</p>
                  <p>Duration: ${prescription.duration}</p>
                  ${prescription.notes ? `<p>Instructions: ${prescription.notes}</p>` : ''}
                </div>
              `).join('')}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b">
          <CardTitle>Add Medication</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Medication</Label>
                <Select
                  value={selectedMed}
                  onValueChange={setSelectedMed}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select medication" />
                  </SelectTrigger>
                  <SelectContent>
                    {medications.map(med => (
                      <SelectItem key={med.id} value={med.id}>
                        {med.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Dosage</Label>
                <Input
                  value={selectedDosage}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedDosage(e.target.value)}
                  placeholder="e.g., 500mg"
                  disabled={!selectedMed}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Input
                  value={frequency}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFrequency(e.target.value)}
                  placeholder="e.g., Twice daily"
                />
              </div>

              <div className="space-y-2">
                <Label>Duration</Label>
                <Input
                  value={duration}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDuration(e.target.value)}
                  placeholder="e.g., 7 days"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Special Instructions</Label>
              <Input
                value={notes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)}
                placeholder="e.g., Take with food"
              />
            </div>

            <Button onClick={handleAdd} className="w-full">
              Add Medication
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader className="border-b flex flex-row items-center justify-between">
          <CardTitle>Current Prescriptions</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            className="flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {value.map((prescription, index) => (
              <div key={index} className="flex items-start justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <p className="font-medium">{prescription.medication} - {prescription.dosage}</p>
                  <p className="text-sm text-gray-600">
                    {prescription.frequency} for {prescription.duration}
                  </p>
                  {prescription.notes && (
                    <p className="text-sm text-gray-500">{prescription.notes}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(index)}
                  className="text-red-500 hover:text-red-600"
                >
                  Remove
                </Button>
              </div>
            ))}
            {value.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                No medications added yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Historical Prescriptions Section */}
      <HistoricalPrescriptionsSection 
        patientId={patientId}
        onPrescriptionStatusChange={onPrescriptionStatusChange}
      />
    </div>
  );
}
